import type { PaletteOptions } from "@mui/material/styles";
import { colorPrimitives as c } from "../tokens/colorPrimitives";
import {
  lightSemantic,
  darkSemantic,
  type SemanticPalette,
} from "../tokens/colorSemantic";

// Inline comments on each primitive list the semantic-token paths that
// use the same color in the same mode. Helps trace from MUI palette
// keys back to the design system tokens in colorSemantic.ts.

const buildPalette = (mode: "light" | "dark"): PaletteOptions => {
  const scheme: SemanticPalette =
    mode === "light" ? lightSemantic : darkSemantic;
  return {
  mode,
  primary: {
    main:
      mode === "light"
        ? c.blue[700] // border/2-dimmed/active, border/5-brand/active, paper/5-brandFilled/hover
        : c.blue[300], // text/interactiveLink, icon/brand, icon/interactiveLink, border/5-brand/active
    light:
      mode === "light"
        ? c.blue[600] // text/interactiveLink, icon/brand, icon/interactiveLink, border/1-surface/hover, border/1-surface/active, border/4-faint/active, border/5-brand/hover, border/6-brandMuted/active, paper/5-brandFilled/rest
        : c.blue[300], // text/interactiveLink, icon/brand, icon/interactiveLink, border/5-brand/active
    dark:
      mode === "light"
        ? c.blue[800] // text/brand, paper/5-brandFilled/active
        : c.blue[200], // text/brand
    contrastText: c.base.white, // text/staticWhite, icon/staticWhite (both modes); text/inverse, icon/inverse, paper/1-surface/rest, paperElevation/10-15 (light only)
  },
  secondary: {
    main: mode === "light" ? c.lightBlue[600] : c.lightBlue[300],
    light: mode === "light" ? c.lightBlue[400] : c.lightBlue[200],
    dark: mode === "light" ? c.lightBlue[800] : c.lightBlue[500],
    contrastText: c.base.white, // text/staticWhite, icon/staticWhite (both modes); text/inverse, icon/inverse, paper/1-surface/rest, paperElevation/10-15 (light only)
  },
  error: {
    main:
      mode === "light"
        ? c.red[700] // text/error, border/8-error/active, paper/8-errorFilled/hover
        : c.red[300], // icon/error, border/8-error/active
    light:
      mode === "light"
        ? c.red[600] // icon/error, border/8-error/hover, paper/8-errorFilled/rest
        : c.red[300], // icon/error, border/8-error/active
    dark:
      mode === "light"
        ? c.red[800] // paper/8-errorFilled/active
        : c.red[200], // text/error
    contrastText: c.base.white, // text/staticWhite, icon/staticWhite (both modes); text/inverse, icon/inverse, paper/1-surface/rest, paperElevation/10-15 (light only)
  },
  warning: {
    main:
      mode === "light"
        ? c.amber[700] // border/12-warning/hover
        : c.amber[300], // icon/warning
    light:
      mode === "light"
        ? c.amber[600] // icon/warning, border/12-warning/rest, paper/12-warningFilled/rest, paper/12-warningFilled/hover
        : c.amber[300], // icon/warning
    dark:
      mode === "light"
        ? c.amber[800] // text/warning, border/12-warning/active, paper/12-warningFilled/active
        : c.amber[200], // text/warning
    contrastText: c.base.white, // text/staticWhite, icon/staticWhite (both modes); text/inverse, icon/inverse, paper/1-surface/rest, paperElevation/10-15 (light only)
  },
  info: {
    main: mode === "light" ? c.lightBlue[700] : c.lightBlue[300],
    light: mode === "light" ? c.lightBlue[600] : c.lightBlue[300],
    dark: mode === "light" ? c.lightBlue[800] : c.lightBlue[200],
    contrastText: c.base.white, // text/staticWhite, icon/staticWhite (both modes); text/inverse, icon/inverse, paper/1-surface/rest, paperElevation/10-15 (light only)
  },
  success: {
    main:
      mode === "light"
        ? c.green[700] // text/success, border/10-success/active, paper/10-successFilled/hover
        : c.green[300], // icon/success, border/10-success/active
    light:
      mode === "light"
        ? c.green[600] // icon/success, border/10-success/hover, paper/10-successFilled/rest
        : c.green[300], // icon/success, border/10-success/active
    dark:
      mode === "light"
        ? c.green[800] // paper/10-successFilled/active
        : c.green[200], // text/success
    contrastText: c.base.white, // text/staticWhite, icon/staticWhite (both modes); text/inverse, icon/inverse, paper/1-surface/rest, paperElevation/10-15 (light only)
  },
  grey: c.gray,
  text: {
    primary: scheme.text.primary,
    secondary: scheme.text.secondary,
    disabled: scheme.text.disabled,
  },
  background: {
    default: scheme.paper["1-surface"].rest,
    paper: scheme.paper["1-surface"].rest,
  },
  divider: scheme.border["1-surface"].rest,
  action: {
    disabled: scheme.stateBorder.disabled,
    disabledBackground: scheme.stateBackground.disabled,
  },
  // Semantic design-system tokens exposed under `theme.vars.palette.rmh.*`
  // as CSS variables that auto-resolve per scheme.
  rmh: scheme,
  };
};

export const lightPalette = buildPalette("light");
export const darkPalette = buildPalette("dark");
